import React, { useState, useEffect, useCallback } from 'react';
import { Heading } from './common/Catalyst/heading';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './common/Catalyst/table';
import { ArrowPathIcon } from '@heroicons/react/24/outline';
import { BankAccountReturn } from './clients/rust-api-client';
import { bankService } from './clients/ApiClients';
import {
  formatCurrency,
  formatDate,
  getBankErrorParts,
} from './utils/bankHelpers';
import { Button } from './common/Catalyst/button';
import { Badge } from './common/Catalyst/badge';
import { Text } from './common/Catalyst/text';
import ShowAlert from './ShowAlert';
import LoadingSpinner from './LoadingSpinner';
import AccountActivityDialog from './AccountActivityDialog';

interface AlertState {
  title: string;
  message: string;
}

// Accounts are read live from the bank, so failures are usually reachability
// (502) rather than the caller's input. Titles reflect that context.
const accountsError = (err: unknown): AlertState => {
  const { status, body } = getBankErrorParts(err);
  switch (status) {
    case 502:
      return {
        title: "Couldn't reach the bank",
        message:
          body ||
          'The accounts could not be loaded because the bank was unreachable. This environment may not be whitelisted for bank access.',
      };
    case 403:
      return {
        title: 'Not authorized',
        message: body || 'You do not have permission to view these accounts.',
      };
    case 404:
      return {
        title: 'Company not found',
        message: body || 'No company was found.',
      };
    default:
      return {
        title: "Couldn't load accounts",
        message: body || 'Please try again.',
      };
  }
};

interface BankAccountListProps {
  companyId: string | null;
}

const BankAccountList: React.FC<BankAccountListProps> = ({ companyId }) => {

  const [accounts, setAccounts] = useState<BankAccountReturn[]>([]);
  const [loading, setLoading] = useState(true);
  const [alert, setAlert] = useState<AlertState | null>(null);
  const [activityAccount, setActivityAccount] =
    useState<BankAccountReturn | null>(null);

  const fetchAccounts = useCallback(async () => {
    if (!companyId) {
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      setAlert(null);
      const response = await bankService.getCompanyBankAccounts({ companyId });
      setAccounts(response.data.accounts);
    } catch (err) {
      console.error('Error fetching bank accounts:', err);
      setAlert(accountsError(err));
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => {
    fetchAccounts();
  }, [fetchAccounts]);

  return (
    <>
      <div className="flex flex-col space-y-4 sm:flex-row sm:items-center sm:justify-between sm:space-y-0 mb-4">
        <Heading level={4}>Bank Accounts</Heading>
        <Button plain onClick={fetchAccounts} disabled={loading}>
          <ArrowPathIcon className="h-5 w-5 mr-2" />
          Refresh
        </Button>
      </div>

      <Text className="mb-4">
        Balances are read live from the bank across the company's active
        connections. Nothing here is cached.
      </Text>

      {alert && (
        <ShowAlert
          alertType="error"
          alertTitle={alert.title}
          alertMessage={alert.message}
          isVisible={!!alert}
          onClose={() => setAlert(null)}
          timeout={8000}
          isInline={true}
        />
      )}

      <div className="bg-stone-100 dark:bg-gray-600 rounded-lg shadow transition-colors duration-200 overflow-hidden">
        <div className="px-4 py-5 sm:p-6">
          {loading ? (
            <LoadingSpinner />
          ) : accounts.length === 0 ? (
            <Text>
              {alert
                ? 'Accounts could not be loaded.'
                : 'No bank accounts are visible for this company yet.'}
            </Text>
          ) : (
            <div className="overflow-x-auto">
              <div className="inline-block min-w-full align-middle">
                <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 sm:rounded-lg">
                  <Table>
                    <TableHead className="bg-stone-100 dark:bg-gray-600">
                      <TableRow>
                        <TableHeader className="text-base font-semibold">
                          Account #
                        </TableHeader>
                        <TableHeader className="text-base font-semibold">
                          Name
                        </TableHeader>
                        <TableHeader className="text-base font-semibold">
                          Connection
                        </TableHeader>
                        <TableHeader className="text-base font-semibold">
                          Type
                        </TableHeader>
                        <TableHeader className="text-base font-semibold text-right">
                          Balance
                        </TableHeader>
                        <TableHeader className="text-base font-semibold">
                          Status
                        </TableHeader>
                        <TableHeader className="text-base font-semibold">
                          As of
                        </TableHeader>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {accounts.map((acct) => (
                        <TableRow
                          key={`${acct.connection_id}-${acct.account_number}`}
                          onClick={() => setActivityAccount(acct)}
                          className="cursor-pointer hover:bg-stone-200 dark:hover:bg-zinc-700 transition-colors duration-150"
                        >
                          <TableCell className="font-medium">
                            {acct.account_number}
                          </TableCell>
                          <TableCell>{acct.account_name || '-'}</TableCell>
                          <TableCell>
                            {acct.connection_label || acct.provider_key}
                          </TableCell>
                          <TableCell>{acct.account_type || '-'}</TableCell>
                          <TableCell className="text-right tabular-nums">
                            {formatCurrency(acct.current_balance)}
                          </TableCell>
                          <TableCell>
                            <Badge
                              color={
                                acct.is_open === false ? 'zinc' : 'lime'
                              }
                            >
                              {acct.status ||
                                (acct.is_open === false ? 'Closed' : 'Open')}
                            </Badge>
                          </TableCell>
                          <TableCell>{formatDate(acct.balance_as_of)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      <AccountActivityDialog
        open={!!activityAccount}
        onClose={() => setActivityAccount(null)}
        title={
          activityAccount
            ? `${activityAccount.account_number}${
                activityAccount.account_name
                  ? ` — ${activityAccount.account_name}`
                  : ''
              }`
            : ''
        }
        scope={
          activityAccount && companyId
            ? {
                kind: 'company',
                companyId,
                accountNumber: activityAccount.account_number,
              }
            : null
        }
      />
    </>
  );
};

export default BankAccountList;
